import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FolderKanban, UserPlus } from 'lucide-react';
import { t } from '@/i18n/ro';
import { useAuth } from '@/context/AuthContext';
import {
  getProject,
  createProject,
  updateProject,
  getAccessibleProjects,
  getProjectRoles,
  addProjectMember,
  type ProjectRole,
} from '@/services/projects.service';
import { apiGet } from '@/services/api';
import {
  Button,
  Card,
  Input,
  DateInput,
  MarkdownEditor,
  Select,
  LoadingState,
} from '@/components/common';
import type { Project, User, HalCollection } from '@/types';

interface ProjectFormData {
  name: string;
  identifier: string;
  description: string;
  public: boolean;
  parentId: number | undefined;
  startDate: string;
  dueDate: string;
  // Câmpuri contract
  partileContractante: string;
  nrInregistrare: string;
  dataSemnarii: string;
  durataContract: string;
  pretContract: string;
}

// Member selection for new projects
interface MemberSelection {
  userId: number;
  userName: string;
  roleIds: number[];
}

export function ProjectForm() {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isEditing = !!projectId && projectId !== 'new';

  const [project, setProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<ProjectRole[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<MemberSelection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    identifier: '',
    description: '',
    public: false,
    parentId: undefined,
    startDate: '',
    dueDate: '',
    // Câmpuri contract
    partileContractante: '',
    nrInregistrare: '',
    dataSemnarii: '',
    durataContract: '',
    pretContract: '',
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const projectsData = await getAccessibleProjects();
        setProjects(projectsData);

        if (isEditing && projectId) {
          const projectData = await getProject(Number(projectId));
          setProject(projectData);

          const customFields = (projectData as any).customFields || {};
          setFormData({
            name: projectData.name,
            identifier: projectData.identifier,
            description: projectData.description?.raw || '',
            public: projectData.public || false,
            parentId: projectData._links.parent?.href
              ? Number(projectData._links.parent.href.match(/\/(\d+)$/)?.[1])
              : undefined,
            // Toate câmpurile custom sunt în customFields
            startDate: customFields.startDate || '',
            dueDate: customFields.dueDate || '',
            partileContractante: customFields.partileContractante || '',
            nrInregistrare: customFields.nrInregistrare || '',
            dataSemnarii: customFields.dataSemnarii || '',
            durataContract: customFields.durataContract || '',
            pretContract: customFields.pretContract || '',
          });
        } else {
          // Load available users and roles for new project
          try {
            const [usersData, rolesData] = await Promise.all([
              apiGet<HalCollection<User>>('/auth/data/users/all'),
              getProjectRoles(),
            ]);
            setAvailableUsers(usersData._embedded?.elements || []);
            setAvailableRoles(rolesData);

            // Find "Admin proiect" role
            const adminRole = rolesData.find(r =>
              r.name.toLowerCase().includes('admin') ||
              r.name.toLowerCase().includes('manager')
            );

            // Pre-select current user as admin
            if (user && adminRole) {
              const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login;
              setSelectedMembers([{
                userId: user.id,
                userName: userName,
                roleIds: [adminRole.id],
              }]);
            }
          } catch (err) {
            console.warn('Failed to load users/roles:', err);
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(t('errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditing, projectId]);

  // Track if identifier was manually edited
  const [identifierManuallyEdited, setIdentifierManuallyEdited] = React.useState(false);

  const handleChange = (
    field: keyof ProjectFormData,
    value: string | number | boolean | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Track if user manually edits identifier
    if (field === 'identifier') {
      setIdentifierManuallyEdited(true);
    }

    // Auto-generate identifier from name (unless manually edited or editing existing project)
    if (field === 'name' && !isEditing && !identifierManuallyEdited) {
      const identifier = (value as string)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 100);
      setFormData((prev) => ({ ...prev, identifier }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const data = {
        name: formData.name,
        identifier: formData.identifier || undefined,
        description: formData.description ? { raw: formData.description } : undefined,
        public: formData.public,
        parent: formData.parentId ? `/api/v3/projects/${formData.parentId}` : undefined,
        // Toate câmpurile custom într-un singur obiect
        customField: {
          startDate: formData.startDate || undefined,
          dueDate: formData.dueDate || undefined,
          partileContractante: formData.partileContractante || undefined,
          nrInregistrare: formData.nrInregistrare || undefined,
          dataSemnarii: formData.dataSemnarii || undefined,
          durataContract: formData.durataContract || undefined,
          pretContract: formData.pretContract || undefined,
        },
      };

      if (isEditing && projectId) {
        await updateProject(Number(projectId), data);
        setSuccessMessage('Proiectul a fost salvat cu succes!');
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const newProject = await createProject(data);

        // Add members to the new project
        if (newProject && selectedMembers.length > 0) {
          for (const member of selectedMembers) {
            try {
              await addProjectMember(newProject.id, member.userId, member.roleIds);
            } catch (memberErr) {
              console.warn('Failed to add member:', memberErr);
            }
          }
        }

        navigate('/proiecte/toate');
      }
    } catch (err: unknown) {
      console.error('Failed to save project:', err);
      const errorObj = err as { message?: string; details?: { message?: string } };
      setError(errorObj.message || errorObj.details?.message || t('errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  const parentOptions = projects
    .filter((p) => !isEditing || p.id !== Number(projectId))
    .map((p) => ({
      value: p.id,
      label: p.name,
    }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/proiecte/toate')}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center">
          <FolderKanban className="w-8 h-8 text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? t('projects.editProject') : t('projects.addProject')}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditing
              ? 'Modificați detaliile proiectului'
              : 'Completați informațiile pentru noul proiect'}
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label={t('projects.fields.name')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="Introduceți numele proiectului"
              />
            </div>

            {/* Identificator - afișat doar la editare */}
            {isEditing && (
              <Input
                label={t('projects.fields.identifier')}
                value={formData.identifier}
                onChange={(e) => handleChange('identifier', e.target.value)}
                placeholder="identificator-unic"
                hint="Identificator unic pentru URL"
                disabled={true}
              />
            )}

            {/* Proiect părinte - afișat doar la editare */}
            {isEditing && (
              <Select
                label="Proiect părinte"
                options={parentOptions}
                value={formData.parentId || ''}
                onChange={(e) =>
                  handleChange(
                    'parentId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                placeholder="Fără proiect părinte"
              />
            )}

            <DateInput
              label="Data de început"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
            />

            <DateInput
              label="Data limită"
              value={formData.dueDate}
              onChange={(e) => handleChange('dueDate', e.target.value)}
            />

            <div className="md:col-span-2">
              <MarkdownEditor
                label={t('projects.fields.description')}
                value={formData.description}
                onChange={(val) => handleChange('description', val)}
                rows={6}
                placeholder="Descrieți proiectul..."
                hint=""
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.public}
                  onChange={(e) => handleChange('public', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">
                    {t('projects.fields.public')}
                  </span>
                  <p className="text-sm text-gray-500">
                    Proiectele publice pot fi vizualizate de oricine
                  </p>
                </div>
              </label>
            </div>
          </div>
        </Card>

        {/* Secțiunea „Date contract" a fost eliminată din formular (cerință CJ).
            Câmpurile rămân încărcate și retrimise neschimbate la salvare,
            ca datele de contract existente să NU fie șterse. */}

        {/* Member Selection for New Projects */}
        {!isEditing && selectedMembers.length > 0 && (
          <Card>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">Membri proiect</h3>
              </div>
              <p className="text-sm text-gray-600">
                Utilizatorul curent va fi adăugat automat ca administrator al proiectului.
              </p>
              <div className="space-y-2">
                {selectedMembers.map((member, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700">
                          {member.userName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.userName}</p>
                        <p className="text-xs text-gray-500">
                          {availableRoles
                            .filter(r => member.roleIds.includes(r.id))
                            .map(r => r.name)
                            .join(', ')}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                      Creator
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/proiecte/toate')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            leftIcon={<Save className="w-4 h-4" />}
            isLoading={isSaving}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
